import {
  MouseEvent, useCallback, useEffect, useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Store, useAppDispatch } from 'src/redux/Store';
import FormDatePicker from 'src/components/Control/FormControls/FormDatePicker';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import FormTextField from 'src/components/Control/FormControls/FormTextField';
import FormCheckbox from 'src/components/Control/FormControls/FormCheckbox';
import FormSingleSelect, { SelectItem } from 'src/components/Control/FormControls/FormSingleSelect';
import Subtitle from 'src/components/UI/Subtitle';
import Header from 'src/components/UI/Header';
import Row from 'src/components/UI/Row';
import Col2 from 'src/components/UI/Col2';
import Title from 'src/components/UI/Title';
import Button from '@material-ui/core/Button';
import Form from 'src/components/UI/Form';
import Scroller from 'src/components/UI/Scroller';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';
import { ErrorBag } from 'src/models/ErrorModel';
import { t, f } from 'src/lib/language';
import ContactSelector from 'src/components/Control/ContactSelector';
import { AUTOCOMPLETE_ID } from 'src/constants/Autocomplete';
import { getContactAction } from 'src/redux/actions/contactActions';
import { ContactPayload } from 'src/models/ContactModel';
import { getContactInfoForInvoice } from 'src/lib/ContactHelper';
import { getProductListAction } from 'src/redux/actions/productAction';
import { ProductList, ProductModel } from 'src/models/ProductModel';
import OrdersDisplayer from 'src/components/Elements/OrderDisplayer';
import ManageProductsButton from 'src/components/Control/Buttons/ManageProductsButton';
import GatewaySelector from 'src/components/Control/GatewaySelector';
import OrganisationBankAccountSelector from 'src/components/Control/OrganisationBankAccountSelector';
import PaymentAccountSelector from 'src/components/Control/PaymentAccountSelector';
import { ProcessingType } from 'src/models/GatewayModel';
import { PAYMENT_TYPE } from 'src/models/PaymentModel';
import { MODALS_SIZE } from 'src/models/modal';
import { closeModal } from 'src/redux/actions/modalActions';
import { MODALS } from 'src/models/ModalModel';
import {
  RECURRING_FREQUENCY,
  RECURRING_FREQUENCY_LIST,
  RecurringPayload,
} from 'src/models/RecurringModel';
import {
  OrderList,
  OrderModel,
  OrderTaxModel,
  ProductOrderList,
  ProductOrderModel,
  TicketInfo,
} from 'src/models/InvoiceModel';
import { INVOICE_ORDER_FIELDS } from 'src/constants/Invoices';
import { postRecurringAction } from 'src/redux/actions/recurringActions';
import RecurringValidator from 'src/validations/RecurringValidator';
import ActionButton, { MENU_BUTTON_CATEGORY } from 'src/components/UI/ActionButton';
import SaveIcon from '@material-ui/icons/Save';
import { Moment } from 'moment';
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment-timezone-all';

const AddRecurringForm = () => {
  const dispatch = useAppDispatch();
  const lang = useSelector((state: Store) => state.language.language ?? 'en');
  const taxes = useSelector((state: Store) => {
    if (!state.taxes) return {};
    const result = {} as { [key: number]: any };
    state.taxes.forEach((tax) => {
      if (tax.id) result[tax.id] = tax;
    });
    return result;
  });
  const products = useSelector((state: Store) => state.products || [] as ProductList);
  const [contactId, setContactId] = useState(0);
  const { contact: contactInfo } = useSelector(
    (state: Store) => {
      if (!!state.contacts[contactId]) {
        return state.contacts[contactId] as ContactPayload;
      }
      return {} as ContactPayload;
    },
  );

  const [recurringState, setRecurringState] = useState({
    title: '',
    amount: 0,
    frequency: RECURRING_FREQUENCY.monthly,
    start_date: moment() as Moment,
    end_date: null as Moment | null,
    contact_id: 0,
    create_invoice: true,
    create_payment: false,
    send_invoice_by_email: true,
    payment_type: PAYMENT_TYPE.cc,
    payment_info_id: 0,
    payment_gateway_id: 0,
    bank_account_id: 0,
  });

  const [orderState, setOrderState] = useState([{
    product_id: 0,
    paid: 0,
    quantity: 1,
    deductible: 0,
    discount: 0,
    taxes: {} as OrderTaxModel,
  }] as ProductOrderList);

  useEffect(() => {
    dispatch(setError({ [FORM.add_recurring]: {} }));
    dispatch(getProductListAction());
  }, []);

  useEffect(() => {
    if (!contactInfo) return;
    const contactInfoForInvoice = getContactInfoForInvoice(contactInfo);
    setRecurringState((prev) => ({
      ...prev,
      ...contactInfoForInvoice,
    }));
  }, [contactInfo]);

  const processing = useSelector(
    (state: Store) => state.formStatus[FORM.add_recurring] === FORM_STATUS.processing,
  );

  const frequencyItems: SelectItem[] = RECURRING_FREQUENCY_LIST.map(
    (frequency) => ({ id: frequency, text: frequency }),
  );

  const paymentTypeItems: SelectItem[] = [
    { id: PAYMENT_TYPE.cc, text: PAYMENT_TYPE.cc },
    { id: PAYMENT_TYPE.eft, text: PAYMENT_TYPE.eft },
  ];

  const getGatewayType = (paymentType: PAYMENT_TYPE) => (
    paymentType === PAYMENT_TYPE.cc ? ProcessingType.cc : ProcessingType.eft
  );

  const onFieldChange = useCallback((value: Value, field: string) => {
    setRecurringState((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const onContactChange = useCallback((id: number) => {
    setContactId(id);
    setRecurringState((prev) => ({ ...prev, contact_id: id }));
    dispatch(getContactAction(id, FORM.add_recurring));
  }, [dispatch]);

  const onDateChange = (field: string) => (value: MaterialUiPickersDate) => {
    setRecurringState((prev) => ({
      ...prev,
      [field]: value ? (value as Moment) : null,
    }));
  };

  const onCreatePaymentChange = (value: boolean) => {
    setRecurringState((prev) => ({ ...prev, create_payment: value }));
    if (value) {
      const selectedOrder = orderState.find((order) => !!order.product_id);
      const product = selectedOrder
        ? products.find((p) => p.id === selectedOrder.product_id)
        : undefined;
      if (product) {
        setRecurringState((prev) => ({
          ...prev,
          bank_account_id: product.bank_account_id || 0,
          payment_gateway_id: prev.payment_type === PAYMENT_TYPE.cc
            ? product.payment_gateway_cc_id || 0
            : product.payment_gateway_eft_id || 0,
        }));
      }
    }
  };

  const getTaxesFromProduct = (product: ProductModel) => {
    const taxesAmount = {} as OrderTaxModel;
    if (!product.taxes) return taxesAmount;
    product.taxes.forEach((taxId: number) => {
      taxesAmount[taxId] = 0;
      if (taxes[taxId]) {
        const price = Number(product.price ?? 0);
        const discount = Number(product.discount ?? 0);
        taxesAmount[taxId] = taxes[taxId].rate * (price - discount);
      }
    });
    return taxesAmount;
  };

  const getTaxesForOrder = (order: ProductOrderModel): string => JSON.stringify(order.taxes);

  const denormalizeOrders = (): OrderList => orderState.map((order: ProductOrderModel) => ({
    ...order,
    deductible: (order.deductible ?? 0) / 100,
    taxes: getTaxesForOrder(order),
  } as OrderModel));

  const onAddProductOrder = useCallback(() => {
    setOrderState((prevOrderState) => [
      ...prevOrderState,
      {
        product_id: 0,
        paid: 0,
        quantity: 1,
        deductible: 0,
      },
    ]);
  }, []);

  const onDeleteProduct = useCallback((orderIndex: number) => {
    setOrderState((prev) => {
      const productOrderList = [...prev];
      productOrderList.splice(orderIndex, 1);
      return productOrderList;
    });
  }, []);

  const onUpdateTickets = useCallback((tickets: TicketInfo[], index: number) => {
    setOrderState((prev) => {
      const orderList = [...prev];
      orderList[index] = { ...orderList[index], tickets };
      return orderList;
    });
  }, []);

  const onUpdateProduct = useCallback((value: Value, field: string, index: number) => {
    setOrderState((prev) => {
      const orderList = [...prev];
      orderList[index][field] = value;
      return orderList;
    });
  }, []);

  const onProductChange = useCallback((product?: ProductModel, index?: number) => {
    if (index === undefined) return;
    setOrderState((prev) => {
      const productOrderList = [...prev];
      if (!product) {
        productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.paid] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.discount] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = 0;
        productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = {} as OrderTaxModel;
        return productOrderList;
      }
      productOrderList[index][INVOICE_ORDER_FIELDS.product_id] = product?.id || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.paid] = product?.price || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.discount] = product?.discount || 0;
      productOrderList[index][INVOICE_ORDER_FIELDS.deductible] = (product?.deductible || 0) * 100;
      productOrderList[index][INVOICE_ORDER_FIELDS.taxes] = getTaxesFromProduct(product);
      setRecurringState((prevRecurring) => ({ ...prevRecurring, amount: product?.price || 0 }));
      return productOrderList;
    });
  }, [taxes]);

  const handleClose = () => {
    dispatch(closeModal({ modal: MODALS.addRecurring }));
  };

  const handleSubmit = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    dispatch(setError({ [FORM.add_recurring]: {} }));
    const hasProductAmount = orderState.some((order) => Number(order.paid) > 0);
    const hasAmount = Number(recurringState.amount) > 0;
    const errorBag = {} as ErrorBag;

    try {
      RecurringValidator(lang, hasProductAmount).validateSync(
        {
          ...recurringState,
          start_date: recurringState.start_date?.format('YYYY-MM-DD'),
          end_date: recurringState.end_date ? recurringState.end_date.format('YYYY-MM-DD') : null,
        },
        { abortEarly: false },
      );
    } catch (validationErrors: any) {
      (validationErrors?.inner || []).forEach((e: any) => {
        if (!errorBag[e.path]) {
          errorBag[e.path] = [];
        }
        errorBag[e.path].push(e.message);
      });
    }

    if (!hasProductAmount && !hasAmount) {
      errorBag.product_id_0 = [t(lang, 'validations.contact.field_required')];
    }

    if (Object.keys(errorBag).length) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        code: t(lang, 'forms.contact.validation_errors'),
      }));
      dispatch(setError({ [FORM.add_recurring]: errorBag }));
      return false;
    }

    const payload = {
      title: recurringState.title,
      amount: Number(recurringState.amount),
      frequency: recurringState.frequency,
      start_date: recurringState.start_date.format('YYYY-MM-DD'),
      end_date: recurringState.end_date ? recurringState.end_date.format('YYYY-MM-DD') : null,
      contact_id: recurringState.contact_id || null,
      create_invoice: recurringState.create_invoice,
      create_payment: recurringState.create_payment,
      send_invoice_by_email: recurringState.send_invoice_by_email,
      invoice_info: recurringState.create_invoice
        ? {
          invoice: {
            title: recurringState.title,
            contact_id: recurringState.contact_id || null,
            amount: Number(recurringState.amount),
          },
          order: denormalizeOrders(),
        }
        : undefined,
      payment_info: recurringState.create_payment
        ? {
          payment_type: recurringState.payment_type,
          payment_info_id: recurringState.payment_info_id || null,
          payment_gateway_id: recurringState.payment_gateway_id || null,
          bank_account_id: recurringState.bank_account_id || null,
        }
        : undefined,
    } as RecurringPayload;

    dispatch(postRecurringAction(payload, true));
    return true;
  };

  return (
    <>
      <Header height="86px">
        <Row>
          <Title>{t(lang, 'menus.add_subscription')}</Title>
          <Button
            style={{ marginLeft: 'auto' }}
            variant="contained"
            color="primary"
            size="small"
            onClick={handleClose}>
            {t(lang, 'misc.close')}
          </Button>
        </Row>
        <Row>
          <ActionButton
            id="subscription-save"
            loading={processing}
            category={MENU_BUTTON_CATEGORY.action}
            onClick={handleSubmit}>
            <SaveIcon />
          </ActionButton>
          <ManageProductsButton />
        </Row>
      </Header>
      <Scroller style={{ paddingRight: '16px', overflowX: 'hidden' }}>
        <Form maxWidth={MODALS_SIZE.large}>
          <Row height="72px" alignItems="flex-start">
            <FormTextField
              form={FORM.add_recurring}
              label={f(lang, 'recurrings.title')}
              name="title"
              onChange={onFieldChange}
              required
              value={recurringState.title || ''} />
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormTextField
                form={FORM.add_recurring}
                label={f(lang, 'recurrings.amount')}
                name="amount"
                type="number"
                onChange={onFieldChange}
                required
                value={recurringState.amount || 0} />
            </Col2>
            <Col2 minHeight="52px">
              <FormSingleSelect
                form={FORM.add_recurring}
                label={f(lang, 'recurrings.frequency')}
                name="frequency"
                items={frequencyItems}
                value={recurringState.frequency}
                noMarginTop
                onChange={(value: RECURRING_FREQUENCY) => onFieldChange(value, 'frequency')} />
            </Col2>
          </Row>
          <Row height="72px" alignItems="flex-start">
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.add_recurring}
                required
                label={f(lang, 'recurrings.start_date')}
                name="start_date"
                onChange={onDateChange('start_date')}
                value={recurringState.start_date} />
            </Col2>
            <Col2 minHeight="52px">
              <FormDatePicker
                form={FORM.add_recurring}
                label={f(lang, 'recurrings.end_date')}
                name="end_date"
                onChange={onDateChange('end_date')}
                value={recurringState.end_date} />
            </Col2>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '8px', marginTop: '16px' }}>
            {t(lang, 'forms.invoices.contacts')}
          </Subtitle>
          <Row height="72px" alignItems="flex-start" style={{ marginBottom: '16px' }}>
            <Col2 minHeight="52px">
              <ContactSelector
                id="subscription-contact-selector"
                autocompleteId={AUTOCOMPLETE_ID.add_recurring_contact}
                onChange={(id: number) => onContactChange(id)}
                label={f(lang, 'recurrings.contact_id')}
                form={FORM.add_recurring}
                name="contact_id"
                contactId={recurringState.contact_id}
                hideEdit={!recurringState.contact_id} />
            </Col2>
          </Row>
          <Subtitle noMargin style={{ marginBottom: '16px' }}>
            {t(lang, 'forms.invoices.orders')}
          </Subtitle>
          <OrdersDisplayer
            form={FORM.add_recurring}
            onUpdateProduct={onUpdateProduct}
            orders={orderState}
            onDeleteProduct={onDeleteProduct}
            onAddProduct={onAddProductOrder}
            onProductChange={onProductChange}
            fundraisingLinked={false}
            onUpdateTickets={onUpdateTickets} />
          <Subtitle noMargin style={{ marginBottom: '8px' }}>
            {f(lang, 'recurrings.state')}
          </Subtitle>
          <Row height="auto">
            <FormCheckbox
              name="create_invoice"
              label={f(lang, 'recurrings.create_invoice')}
              checked={recurringState.create_invoice}
              onChange={onFieldChange} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="create_payment"
              label={f(lang, 'recurrings.create_payment')}
              checked={recurringState.create_payment}
              onChange={(value: boolean) => onCreatePaymentChange(value)} />
          </Row>
          <Row height="auto">
            <FormCheckbox
              name="send_invoice_by_email"
              label={f(lang, 'recurrings.send_invoice_by_email')}
              checked={recurringState.send_invoice_by_email}
              onChange={onFieldChange} />
          </Row>
          { recurringState.create_payment && (
            <>
              <Subtitle noMargin style={{ marginBottom: '8px' }}>
                {t(lang, 'forms.invoices.payment')}
              </Subtitle>
              <Row height="72px" alignItems="flex-start">
                <Col2 minHeight="52px">
                  <FormSingleSelect
                    form={FORM.add_recurring}
                    label={f(lang, 'recurrings.payment_info')}
                    name="payment_type"
                    items={paymentTypeItems}
                    value={recurringState.payment_type}
                    noMarginTop
                    onChange={(value: PAYMENT_TYPE) => onFieldChange(value, 'payment_type')} />
                </Col2>
                <Col2 minHeight="52px">
                  <PaymentAccountSelector
                    value={recurringState.payment_info_id || undefined}
                    type={recurringState.payment_type}
                    onChange={(id: number) => onFieldChange(id, 'payment_info_id')}
                    form={FORM.add_recurring}
                    name="payment_info_id"
                    gatewayId={recurringState.payment_gateway_id || 0}
                    contactId={recurringState.contact_id || 1} />
                </Col2>
              </Row>
              <Row height="72px" alignItems="flex-start" style={{ marginTop: '8px' }}>
                <Col2 minHeight="52px">
                  <OrganisationBankAccountSelector
                    value={recurringState.bank_account_id || undefined}
                    onChange={(id: number) => onFieldChange(id, 'bank_account_id')}
                    form={FORM.add_recurring}
                    name="bank_account_id"
                    noMarginTop
                    noMinWidth />
                </Col2>
                <Col2 minHeight="52px">
                  <GatewaySelector
                    form={FORM.add_recurring}
                    name="payment_gateway_id"
                    onChange={(value: number) => onFieldChange(value, 'payment_gateway_id')}
                    type={getGatewayType(recurringState.payment_type)}
                    value={recurringState.payment_gateway_id}
                    noMarginTop
                    noMinWidth />
                </Col2>
              </Row>
            </>
          )}
        </Form>
      </Scroller>
    </>
  );
};

export default AddRecurringForm;
